/**
 * What the login page is allowed to say, given how the host resolved.
 * An unidentifiable host gets no product claim; an outage still gets a form.
 */
import type { TenantResolution } from '@/features/platform/api';
import { primaryLoginProduct, type LoginProduct } from './login-product';

export type LoginPageState =
  | { kind: 'loading' }
  | { kind: 'product'; product: LoginProduct }
  /** Resolved, but holding no product this portal has copy for. */
  | { kind: 'noProduct' }
  /** No `TenantDomain` row for this host — a misconfiguration. */
  | { kind: 'unmapped' }
  /** `/tenants/resolve` could not be asked. Never a lockout. */
  | { kind: 'unavailable' };

export function loginPageState(
  resolution: TenantResolution,
  isLoading: boolean
): LoginPageState {
  if (isLoading) return { kind: 'loading' };
  if (resolution.status === 'unmapped') return { kind: 'unmapped' };
  if (resolution.status === 'unavailable') return { kind: 'unavailable' };

  // `looksLikeResolveResponse` only checks `tenantId`, so this array is typed
  // but never validated. A renamed or dropped wire field would otherwise turn
  // every host into `noProduct` and withdraw sign-in portal-wide — an outage
  // arriving through the `resolved` door. An honestly empty array is fine.
  const { enabledProducts } = resolution.tenant;
  if (!Array.isArray(enabledProducts)) return { kind: 'unavailable' };

  const product = primaryLoginProduct(enabledProducts);
  return product ? { kind: 'product', product } : { kind: 'noProduct' };
}

/** Whether this state may offer credentials at all. */
export const allowsSignIn = (state: LoginPageState): boolean =>
  state.kind === 'product' || state.kind === 'unavailable';

/** Whether the page may name a tenant — `BrandingProvider` would guess one. */
export const namesTenant = (state: LoginPageState): boolean =>
  state.kind !== 'unmapped' && state.kind !== 'loading';
